import type { IntlShape } from 'react-intl'
import qs from 'qs'
import Places from 'constants/places'
import { buildHotelUrl } from '../TrendingDestinations/utils'
import {
  isLocalDestinationOfferAllFilter,
  LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
  LOCAL_DESTINATION_OFFER_EUROPE_DESTINATION_ID,
} from './localDestinationOfferCarouselConstants'

export type CountryDestination = {
  destinationId: string
}

export type DestinationInput = {
  destinationId?: string
}

export type LocalDestinationOfferCarouselFilterOption = {
  value: string
  label: string
}

export type LocalDestinationOfferCarouselViewModel = {
  destinations: Array<CountryDestination>
  destinationFilterOptions: Array<LocalDestinationOfferCarouselFilterOption>
  defaultDestinationId: string | undefined
}

type EuropeanCountryDestination = CountryDestination & { regionCode: string }

const EUROPEAN_COUNTRY_DESTINATIONS: Array<EuropeanCountryDestination> = [
  { destinationId: Places.Greece.id, regionCode: 'GR' },
  { destinationId: Places.UnitedKingdom.id, regionCode: 'GB' },
  { destinationId: Places.Spain.id, regionCode: 'ES' },
  { destinationId: Places.Italy.id, regionCode: 'IT' },
  { destinationId: Places.France.id, regionCode: 'FR' },
  { destinationId: Places.Germany.id, regionCode: 'DE' },
]

export function getCountryDestinationsWithIds(
  destinations: Array<DestinationInput>,
): Array<CountryDestination> {
  return destinations.flatMap((destination) => {
    if (!destination.destinationId) {
      return []
    }

    return [{
      destinationId: destination.destinationId,
    }]
  })
}

/**
 * Use the approved Europe country list for pills. GB and DE lead with their local country;
 * all other storefronts retain the supplied merchandising order.
 */
export function getEuropeanCountryDestinations(regionCode?: string): Array<CountryDestination> {
  const localDestination = EUROPEAN_COUNTRY_DESTINATIONS.find(destination => destination.regionCode === regionCode)

  if (!localDestination || (regionCode !== 'GB' && regionCode !== 'DE')) {
    return EUROPEAN_COUNTRY_DESTINATIONS.map(({ regionCode: _regionCode, ...destination }) => destination)
  }

  return [
    localDestination,
    ...EUROPEAN_COUNTRY_DESTINATIONS.filter(destination => destination !== localDestination),
  ].map(({ regionCode: _regionCode, ...destination }) => destination)
}

export function mapDestinationsToFilterOptions(
  destinations: Array<CountryDestination>,
  labelsByDestinationId: Record<string, string | undefined>,
): Array<LocalDestinationOfferCarouselFilterOption> {
  return destinations.flatMap(destination => {
    const label = labelsByDestinationId[destination.destinationId]
    if (!label) {
      return []
    }

    return [{
      value: destination.destinationId,
      label,
    }]
  })
}

function formatLocalDestinationOfferAllLabel(intl: IntlShape): string {
  return intl.formatMessage({
    id: '8k4GtkJzy5gh656EioOW',
    defaultMessage: 'All',
    description: 'Filter chip label for showing hotel offers across all eligible European countries on the homepage Europe offer carousel',
  })
}

/**
 * Prefer settled hero offer ids. Ignore placeholder `{ offerIds: [], fetching: true }` lists
 * so `??` does not treat an empty array as a successful exclusion set.
 *
 * Returns `null` while the hero is still unknown and there is no fallback, so the Europe
 * row does not paint with an empty exclusion set and then drop hero duplicates later.
 */
export function resolveLocalDestinationOfferCarouselExcludedIds(
  heroOfferList: Pick<App.OfferList, 'offerIds' | 'fetching'> | undefined,
  fallbackOfferIds: Array<string>,
): Array<string> | null {
  if (heroOfferList && !heroOfferList.fetching && heroOfferList.offerIds.length > 0) {
    return heroOfferList.offerIds
  }

  const heroStillUnknown = !heroOfferList || heroOfferList.fetching
  if (heroStillUnknown && fallbackOfferIds.length === 0) {
    return null
  }

  return fallbackOfferIds
}

function buildAllDestinationFilterOption(intl: IntlShape): LocalDestinationOfferCarouselFilterOption {
  return {
    value: LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
    label: formatLocalDestinationOfferAllLabel(intl),
  }
}

export function buildLocalDestinationOfferCarouselViewModel(
  intl: IntlShape,
  destinationsInput: Array<DestinationInput>,
  labelsByDestinationId: Record<string, string | undefined>,
): LocalDestinationOfferCarouselViewModel {
  const destinations = getCountryDestinationsWithIds(destinationsInput)
  const destinationFilterOptions = destinations.length > 0
    ? [buildAllDestinationFilterOption(intl), ...mapDestinationsToFilterOptions(destinations, labelsByDestinationId)]
    : []
  const defaultDestinationId = destinations.length > 0
    ? LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE
    : undefined

  return {
    destinations,
    destinationFilterOptions,
    defaultDestinationId,
  }
}

export function buildLocalDestinationViewAllUrl(
  destinations: Array<CountryDestination>,
  selectedDestinationId: string | undefined,
  labelsByDestinationId: Record<string, string | undefined>,
): string | undefined {
  if (isLocalDestinationOfferAllFilter(selectedDestinationId)) {
    const europeDestinationName = labelsByDestinationId[LOCAL_DESTINATION_OFFER_EUROPE_DESTINATION_ID]
    if (!europeDestinationName) {
      return undefined
    }

    return `/search?${qs.stringify({
      destinationName: europeDestinationName,
      destinationId: LOCAL_DESTINATION_OFFER_EUROPE_DESTINATION_ID,
      adults: 2,
      children: 'none',
      flexibleWithDate: true,
    })}`
  }

  const destination = destinations.find(item => item.destinationId === selectedDestinationId)
  const destinationName = destination ? labelsByDestinationId[destination.destinationId] : undefined
  if (!destination?.destinationId || !destinationName) {
    return undefined
  }
  return buildHotelUrl(destinationName, destination.destinationId)
}

export function isLocalDestinationOfferFilterSelectionValid(
  selectedFilter: string | undefined,
  destinations: Array<CountryDestination>,
): boolean {
  if (!selectedFilter) {
    return false
  }

  if (isLocalDestinationOfferAllFilter(selectedFilter)) {
    return destinations.length > 0
  }

  return destinations.some(destination => destination.destinationId === selectedFilter)
}
